import { useState, useEffect } from "react";
import axios from "axios";
import API_BASE_URL from "../../../../config/api";
import { useAuth } from "../../../../hooks/AuthProvider";

const BOT_TASKS = [
  "AUTO_REPLY",
  "APPOINTMENT_BOOKING",
  "ORDER_MANAGEMENT",
  "CONTENT_MODERATION",
  "TRANSLATION_ASSISTANT",
  "ROOM_WELCOMER",
  "DISCUSSION_PROMPTER",
  "LIVE_TRANSCRIPTION",
  "LIVE_TRANSLATION",
  "STREAM_ENHANCER",
  "SESSION_MODERATOR",
  "SESSION_SUMMARIZER"
];

const BOT_COMPATIBILITIES = [
  "PERSONAL_ONLY",
  "GROUP_ONLY",
  "ROOM_ONLY",
  "UNIVERSAL"
];

const BOT_SCOPES = ["GLOBAL", "LOCAL"];

const AddBot = ({ onSuccess, onCancel, editBot }) => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    type: "AUTO_REPLY",
    compatibility: "UNIVERSAL",
    scope: "LOCAL",
    price: 0,
    systemPrompt: "",
    iconUrl: "",
    maxSlots: 1
  });

  useEffect(() => {
    if (editBot) {
      setFormData({
        name: editBot.name || "",
        description: editBot.description || "",
        type: editBot.type || "AUTO_REPLY",
        compatibility: editBot.compatibility || "UNIVERSAL",
        scope: editBot.scope || "LOCAL",
        price: editBot.price || 0,
        systemPrompt: editBot.systemPrompt || "",
        iconUrl: editBot.iconUrl || "",
        maxSlots: editBot.maxSlots || 1
      });
    }
  }, [editBot]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: name === "price" || name === "maxSlots" ? parseInt(value) || 0 : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const url = editBot
        ? `${API_BASE_URL}/api/v1/bot/${editBot.id}`
        : `${API_BASE_URL}/api/v1/bot`;

      const method = editBot ? "put" : "post";

      await axios[method](url, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      onSuccess();
    } catch (err) {
      alert(err.response?.data?.message || err.message);
    }
    setLoading(false);
  };

  return (
    <div className="addBotContainer">
      <h3>{editBot ? "Edit Bot Template" : "Create New Bot"}</h3>
      <form onSubmit={handleSubmit}>
        <div className="row">
          <div className="col-md-6">
            <div className="addBotGroup">
              <label>Bot Name</label>
              <input
                type="text"
                name="name"
                className="addBotInput"
                value={formData.name}
                onChange={handleChange}
                required
                placeholder="e.g. Solo Welcomer"
              />
            </div>
          </div>
          <div className="col-md-6">
            <div className="addBotGroup">
              <label>Icon URL</label>
              <input
                type="text"
                name="iconUrl"
                className="addBotInput"
                value={formData.iconUrl}
                onChange={handleChange}
                placeholder="https://..."
              />
            </div>
          </div>
        </div>

        <div className="addBotGroup">
          <label>Description</label>
          <textarea
            name="description"
            className="addBotTextarea"
            rows="2"
            value={formData.description}
            onChange={handleChange}
            required
            placeholder="Briefly explain what this bot does..."
          ></textarea>
        </div>

        <div className="row">
          <div className="col-md-4">
            <div className="addBotGroup">
              <label>Bot Task / Purpose</label>
              <select
                name="type"
                className="addBotSelect"
                value={formData.type}
                onChange={handleChange}
              >
                {BOT_TASKS.map(t => <option key={t} value={t}>{t.replace(/_/g, ' ')}</option>)}
              </select>
            </div>
          </div>
          <div className="col-md-4">
            <div className="addBotGroup">
              <label>Compatibility</label>
              <select
                name="compatibility"
                className="addBotSelect"
                value={formData.compatibility}
                onChange={handleChange}
              >
                {BOT_COMPATIBILITIES.map(c => <option key={c} value={c}>{c.replace(/_/g, ' ')}</option>)}
              </select>
            </div>
          </div>
          <div className="col-md-4">
            <div className="addBotGroup">
              <label>Scope</label>
              <select
                name="scope"
                className="addBotSelect"
                value={formData.scope}
                onChange={handleChange}
              >
                {BOT_SCOPES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          </div>
        </div>

        <div className="row">
          <div className="col-md-6">
            <div className="addBotGroup">
              <label>Price (SoloCoins)</label>
              <input
                type="number"
                name="price"
                className="addBotInput"
                value={formData.price}
                onChange={handleChange}
                min="0"
              />
            </div>
          </div>
          <div className="col-md-6">
            <div className="addBotGroup">
              <label>Max Slots (Chats per purchase)</label>
              <input
                type="number"
                name="maxSlots"
                className="addBotInput"
                value={formData.maxSlots}
                onChange={handleChange}
                min="1"
              />
            </div>
          </div>
        </div>

        <div className="addBotGroup">
          <label>System Prompt (The Bot's Brain)</label>
          <textarea
            name="systemPrompt"
            className="addBotTextarea"
            rows="6"
            value={formData.systemPrompt}
            onChange={handleChange}
            required
            placeholder="You are an AI assistant that..."
          ></textarea>
          <small className="text-muted">This defines the persona and rules the AI follows.</small>
        </div>

        <div className="botParameterGuide mt-4 p-4 border rounded bg-light">
          <h5 className="mb-3 text-primary">Technical Parameter Guide</h5>
          <div className="row g-3">
            <div className="col-md-6">
              <h6 className="fw-bold mb-1">Bot Task</h6>
              <p className="small text-muted">Determines the functional logic. e.g., <code>ROOM_WELCOMER</code> greets members, <code>CONTENT_MODERATION</code> scans for spam.</p>
            </div>
            <div className="col-md-6">
              <h6 className="fw-bold mb-1">Compatibility</h6>
              <p className="small text-muted">Defines where the bot can be installed. <code>UNIVERSAL</code> works in 1-on-1, groups, and rooms.</p>
            </div>
            <div className="col-md-6">
              <h6 className="fw-bold mb-1">Scope</h6>
              <p className="small text-muted"><code>GLOBAL</code> affects all users in the chat (Admin only). <code>LOCAL</code> only affects the user who purchased it.</p>
            </div>
            <div className="col-md-6">
              <h6 className="fw-bold mb-1">System Prompt</h6>
              <p className="small text-muted">The core instructions. Define identity, tone, and strict constraints (e.g., "Do not mention competitors").</p>
            </div>
            <div className="col-md-6">
              <h6 className="fw-bold mb-1">Max Slots</h6>
              <p className="small text-muted">How many concurrent chats a single purchase covers. A value of 1 means one active chat at a time.</p>
            </div>
            <div className="col-md-6">
              <h6 className="fw-bold mb-1">Price</h6>
              <p className="small text-muted">The SoloCoin cost charged to the user's wallet upon purchase from the Marketplace.</p>
            </div>
          </div>
        </div>

        <div className="addBotFooter">
          <button type="submit" className="addBotSaveBtn" disabled={loading}>
            {loading ? "Saving..." : editBot ? "Update Bot" : "Create Bot"}
          </button>
          <button type="button" className="addBotCancelBtn" onClick={onCancel}>
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};

export default AddBot;
